import type { ReactElement } from 'react';
import { Box, Group, SimpleGrid, Text } from '@mantine/core';
import type { LucideIcon } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SurfaceCard } from '@/design/SurfaceCard';
import { scoreBand } from '@/design/ScoreColors';
import AdoptionScoreBar from './AdoptionScoreBar';
import { DRIVER_ICONS } from './AdoptionDriverIcons';
import type { AdoptionScore, AdoptionTrend } from './AdoptionTypes';

interface AdoptionDriverChartsProps {
    score: AdoptionScore;
    trend: AdoptionTrend;
    /** Show the weekly area charts; hidden when the date range is too short to trend (≤ 1 week). */
    showTrend: boolean;
}

/**
 * One driver's card: icon, current value, weight, description, score bar, and —
 * when the range spans more than a week — a weekly area chart. Everything is
 * colored by the score's maturity band, so the card's accent matches its bar.
 */
function DriverChart({
    label,
    description,
    weight,
    value,
    labels,
    values,
    icon: Icon,
    showTrend,
}: {
    label: string;
    description: string;
    weight: number;
    /** The headline (trailing-4-week) score shown as the big number — matches the overview tiles. */
    value: number;
    labels: string[];
    values: number[];
    icon: LucideIcon;
    showTrend: boolean;
}): ReactElement {
    const band = scoreBand(value);
    const color = band.color;
    const data = labels.map((l, i) => ({ label: l, value: values[i] ?? 0 }));
    const ticks =
        labels.length > 2 ? [labels[0], labels[Math.floor(labels.length / 2)], labels[labels.length - 1]].filter((t): t is string => t !== undefined) : labels;

    return (
        <SurfaceCard $padding="md" style={{ borderTop: `2px solid ${color}` }}>
            <Group justify="space-between" align="flex-start" mb={6} wrap="nowrap">
                <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
                    <Box
                        style={{
                            width: 26,
                            height: 26,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: band.soft,
                            border: `1px solid color-mix(in srgb, ${color} 28%, transparent)`,
                            borderRadius: 'var(--bm-radius)',
                        }}
                    >
                        <Icon size={14} color={color} />
                    </Box>
                    <Text fw={700} style={{ fontSize: 14 }} truncate>
                        {label}
                    </Text>
                </Group>
                <Text c="dimmed" fw={600} style={{ fontSize: 11, flexShrink: 0 }}>
                    weight {weight}%
                </Text>
            </Group>
            <Group align="baseline" gap="xs" wrap="nowrap">
                <Text fw={700} ff="monospace" style={{ fontSize: 24, color, letterSpacing: '-0.02em' }}>
                    {value}
                </Text>
                <Text c="dimmed" style={{ fontSize: 12 }} lineClamp={1}>
                    {description}
                </Text>
            </Group>
            <Box mt={8}>
                <AdoptionScoreBar value={value} />
            </Box>
            {showTrend && (
                <Box mt={6} style={{ height: 96 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
                            <defs>
                                <linearGradient id={`adoption-fill-${label}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <YAxis hide domain={[0, 100]} />
                            <XAxis dataKey="label" ticks={ticks} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: 'var(--bm-color-text-dimmed)' }} interval="preserveStartEnd" />
                            <Tooltip
                                cursor={{ stroke: color, strokeOpacity: 0.3 }}
                                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--bm-color-border)', background: 'var(--bm-color-body)' }}
                                formatter={(cell: number) => [cell, label]}
                            />
                            <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#adoption-fill-${label})`} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </SurfaceCard>
    );
}

/** The four driver cards, honoring the currently selected scope in `trend`. */
export default function AdoptionDriverCharts({ score, trend, showTrend }: AdoptionDriverChartsProps): ReactElement {
    const byKey = new Map(trend.drivers.map((d) => [d.key, d]));
    return (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {score.drivers.map((driver) => {
                const entry = byKey.get(driver.key);
                return (
                    <DriverChart
                        key={driver.key}
                        label={driver.label}
                        description={driver.description}
                        weight={driver.weight}
                        value={entry?.score ?? driver.score}
                        labels={trend.labels}
                        values={entry?.weekly ?? driver.weekly}
                        icon={DRIVER_ICONS[driver.key]}
                        showTrend={showTrend}
                    />
                );
            })}
        </SimpleGrid>
    );
}
